import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { DataGridItem } from "components/data-grid";
import { injectIntl } from "react-intl";
import { CardWithDelete } from "components/card";
import { getAssetQuery } from "pages/asset/details-page";
import { useAuthDataContext } from "components/auth";
import { useQuery } from "components/graphql/query/use-query";
import FormConfirm from "components/form/form-confirm";
import ScreenPosition from "components/screen-position";
import { LayoutAssetForm, LayoutAssetSendToForm, LayoutAssetPosAndSizeForm } from "./partial";
import { AssetFormTypeProvider, useAssetFormTypeContext } from "pages/asset";
import { getBase64Extension } from "components/thumbnail";
import Checkbox from "@material-ui/core/Checkbox";
import Icon from "components/icon";
import isEqual from "lodash/isEqual";
import { AssetDisplay } from "components/thumbnail";
import withStyles from "components/with-styles";
import styles from "./styles/layout-form.css.js";
import IconButton from "components/icon-button";

const checkIsPng = layoutImage => {
  if (typeof layoutImage !== "string") {
    return false;
  }
  return "image/png" === getBase64Extension(layoutImage);
};

const LayoutCompactedHeader = ({ layout, classes, customerId }) => {
  return layout.assetId ? (
    <>
      <AssetDisplay
        className={classes.header__preview}
        asset={{ id: layout.assetId, customerId, format: layout.asset?.format }}
        width={50}
      />
      <div className={classes.header__preview__codename}>{layout.assetIdCodename}</div>
    </>
  ) : (
    <>
      <img
        className={classes.header__preview}
        style={{ height: 50 }}
        src={layout?.asset?.image}
        alt={layout?.asset?.codename}
      />
      <div className={classes.header__preview__codename}>{layout?.asset?.codename}</div>
    </>
  );
};

LayoutCompactedHeader.propTypes = {
  /**
   * Objeto con los valores que se quieren representar
   */
  layout: PropTypes.object,
  /**
   * Id de customer
   */
  customerId: PropTypes.string,
};

const GraphicSetLayout = ({
  graphicsetSchedules,
  index,
  collectionName,
  element: layout,
  newElementAdded,
  onChange: handleChange,
  onDelete,
  intl,
  excluded,
  onAssetChange,
  removeFromExclusions,
  addToExclusions,
  customerId,
  isSelected,
  onSelected,
  classes,
  compacted,
}) => {
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(layout.assetIdCategoryIds || []);
  const isFirst = useRef(true);
  const [isOpenDialog, setIsOpenDialog] = useState(newElementAdded);
  const [isOpen, setIsOpen] = useState(false);
  const [assetCustomerId, setAssetCustomerId] = useState(null);
  const [assetFormat, setAssetFormat] = useState(null);
  const [isPng, setIsPng] = useState(false);

  const { hasAdminPrivileges } = useAuthDataContext();
  const { type } = useAssetFormTypeContext();
  const query = useMemo(() => getAssetQuery(layout.assetId, customerId, hasAdminPrivileges), [
    customerId,
    hasAdminPrivileges,
    layout.assetId,
  ]);
  const { data, loading, error } = useQuery(query);

  const layoutPath = `${collectionName}.${index}`;

  const handleLayoutDelete = useCallback(
    () => {
      removeFromExclusions(layout.assetId);
      onDelete();
    },
    [layout.assetId, onDelete, removeFromExclusions],
  );

  useEffect(
    () => {
      if (!loading && data && data.asset && layout.assetId) {
        setAssetCustomerId(data.asset.customerId);
        setAssetFormat(data.asset.format);
        if (!isFirst.current) {
          handleChange(`${layoutPath}.size`)(data.asset.sizePx);
          handleChange(`${layoutPath}.originalSize`)(data.asset.sizePx);
          handleChange(`${layoutPath}.assetIdCodename`)(data.asset.codename);
          handleChange(`${layoutPath}.format`)(data.asset.format);
        }
      }
    },
    [data, handleChange, layout.assetId, layoutPath, loading],
  );

  useEffect(
    () => {
      if (isOpen) {
        setAssetFormat("");
        setSelectedCategoryIds(null);
      }
    },
    [isOpen],
  );

  useEffect(
    () => {
      if (type !== layout.type) {
        handleChange(`${layoutPath}.type`)(type);
      }
    },
    [handleChange, layout.type, layoutPath, type],
  );

  const commonProps = {
    layout: {
      ...layout,
      asset: {
        ...layout.asset,
        format: assetFormat,
      },
    },
    layoutPath: `${layoutPath}`,
    handleChange,
  };

  useEffect(
    () => {
      if (layout.asset) {
        setIsPng(checkIsPng(layout.asset.image));
      }
    },
    [assetFormat, layout],
  );

  useEffect(() => {
    isFirst.current = false;
  }, []);

  const onlyPng = (isOpen && type === "image" && isPng) || (!isOpen && assetFormat && assetFormat === "png");
  const isImage = (isOpen && type === "image") || (!isOpen && assetFormat && ["png", "apng"].includes(assetFormat));
  const isFileSelected = (layout.asset && layout.asset.image) || assetFormat;

  return (
    <CardWithDelete
      header={
        <div className={classes.header}>
          <Icon className={cn("dragHandle", classes.header__handle)} name="drag-indicator" />
          <Checkbox
            className={classes.header__checkbox}
            id={`selectedLayout${layout.id}`}
            name={`selectedLayout${layout.id}`}
            value={`selectedLayout${layout.id}`}
            checked={isSelected(layout)}
            onChange={event => onSelected(layout, event.target.checked)}
          />
          {compacted && <LayoutCompactedHeader layout={layout} customerId={customerId} classes={classes} />}
        </div>
      }
      onDelete={handleLayoutDelete}
    >
      <FormConfirm show={error} error label={intl.formatMessage({ id: "common.graphql.error" })} />

      {compacted && (
        <div className={classes.dashboard_menu_drawer_icon}>
          <IconButton onClick={() => setIsOpenDialog(!isOpenDialog)}>
            <Icon name={isOpenDialog ? "keyboard-arrow-up" : "keyboard-arrow-down"} />
          </IconButton>
        </div>
      )}

      {(!compacted || isOpenDialog) && (
        <>
          <LayoutAssetForm
            selectedCategoryIds={selectedCategoryIds}
            setSelectedCategoryIds={setSelectedCategoryIds}
            customerId={customerId}
            excluded={excluded}
            addToExclusions={addToExclusions}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            {...commonProps}
          />
          {isFileSelected && (
            <LayoutAssetSendToForm disabled={!isImage} graphicsetSchedules={graphicsetSchedules} {...commonProps} />
          )}
          {onlyPng && (
            <>
              <LayoutAssetPosAndSizeForm {...commonProps} />
              <DataGridItem big>
                <ScreenPosition layouts={[{ ...layout, asset: { ...layout.asset, customerId: assetCustomerId } }]} />
              </DataGridItem>
            </>
          )}
        </>
      )}
    </CardWithDelete>
  );
};

GraphicSetLayout.propTypes = {
  /**
   * Índice del elemento en la colección
   */
  index: PropTypes.number,
  /**
   * Nombre de la colección
   */
  collectionName: PropTypes.string,
  /**
   * Función que se invoca al modificar algún valor
   */
  onChange: PropTypes.func,
  /**
   * Función que se invoca al modificar el asset
   */
  onAssetChange: PropTypes.func,
  /**
   * Función que se invoca al eliminar el layout
   */
  onDelete: PropTypes.func,
  /**
   * Función que elimina el assetId de las exclusions
   */
  removeFromExclusions: PropTypes.func,
  /**
   * Función que añade el assetId a las exclusions
   */
  addToExclusions: PropTypes.func,
  /**
   * Objeto con los valores que se quieren representar
   */
  element: PropTypes.object,
  /**
   * Indica si se ha creado un elemento recientemente
   */
  newElementAdded: PropTypes.bool,
  /**
   * Valores a excluir del selector
   */
  excluded: PropTypes.array,
  /*
   * CustomerId del graphicset
   */
  customerId: PropTypes.string,
  /**
   * Schedules del graphicset
   */
  graphicsetSchedules: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      period: PropTypes.number.isRequired,
    }),
  ),
  /*
   * Comprueba si un layout tiene el checkbox seleccionado
   */
  isSelected: PropTypes.func,
  /*
   * Callback que se llama cuando se clica el checkbox de un layout
   */
  onSelected: PropTypes.func,
  /*
   * Muestra la vista compacta
   */
  compacted: PropTypes.bool,
};

const TypedGraphicsetLayout = injectIntl(
  withStyles(styles)(props => (
    <AssetFormTypeProvider>
      <GraphicSetLayout {...props} />
    </AssetFormTypeProvider>
  )),
);

export default React.memo(TypedGraphicsetLayout, (prevProps, nextProps) => {
  // eslint-disable-next-line no-unused-vars
  const { prevSegments, ...prevElement } = prevProps.element;
  // eslint-disable-next-line no-unused-vars
  const { nextSegments, ...nextElement } = nextProps.element;

  return (
    isEqual({ ...prevProps, element: prevElement }, { ...nextProps, element: nextElement }) &&
    prevProps.isSelected(prevElement) === nextProps.isSelected(nextElement)
  );
});
